import React, { useEffect, useRef, useState } from 'react';
import A from '../../images/a.webp';
import B from '../../images/b.webp';
import C from '../../images/c.webp';
import D from '../../images/d.webp';
import Project05 from '../../images/project-05.webp';
import Project06 from '../../images/project-06.webp';
import Project07 from '../../images/project-07.webp';
import Project08 from '../../images/project-08.webp';
import Project09 from '../../images/project-09.webp';
import Project10 from '../../images/project-10.webp';
import Project11 from '../../images/project-11.webp';
import Project12 from '../../images/project-12.webp';
import './project.css';

const projectGroups = [
  [
    { image: A, alt: 'Kodex Solutions website', title: 'Kodex Solutions', category: 'Corporate website', challenge: 'Present a broad digital offering with a clear, credible and modern identity.', solution: 'A responsive brand website with focused messaging, structured services and direct contact paths.', result: 'A stronger digital presence that makes the company easier to understand and contact.', services: ['UX/UI design', 'Frontend development', 'Responsive design'] },
    { image: B, alt: 'Developer portfolio website', title: 'Developer Portfolio', category: 'Personal brand', challenge: 'Bring experience, skills and selected work together in one professional destination.', solution: 'A focused portfolio with clear hierarchy, personal positioning and accessible contact options.', result: 'A memorable personal brand designed to support new opportunities and collaborations.', services: ['Personal branding', 'Web design', 'Frontend development'] },
    { image: C, alt: 'Fitness platform website', title: 'Fitness Platform', category: 'Digital product', challenge: 'Turn a high-energy fitness concept into a clear and motivating digital experience.', solution: 'A bold interface with strong calls to action and a visual structure built around conversion.', result: 'A scalable concept that can grow into memberships, programs and digital services.', services: ['Product design', 'UX strategy', 'Responsive development'] },
    { image: D, alt: 'Restaurant website concept', title: 'Restaurant Concept', category: 'Hospitality', challenge: 'Create a premium first impression while making the offer simple to explore.', solution: 'A visual restaurant experience combining strong imagery, elegant typography and clear navigation.', result: 'A refined presentation designed to increase interest, bookings and brand recognition.', services: ['Creative direction', 'Web design', 'Responsive development'] },
  ],
  [
    { image: Project05, alt: 'Hud and Fot clinic website concept', title: 'Hud & Fotkliniken', category: 'Beauty & wellness', challenge: 'Make a wide treatment offering feel calm, clear and easy to book.', solution: 'A warm editorial design with structured treatments, strong trust signals and direct booking paths.', result: 'A premium concept that helps visitors find the right treatment faster.', services: ['UX/UI design', 'Service structure', 'Booking journey'] },
    { image: Project06, alt: 'Jakobsbergs driving school website concept', title: 'Jakobsbergs Trafikskola', category: 'Education', challenge: 'Explain the path to a driving licence and turn interest into lesson bookings.', solution: 'A confident high-contrast website with clear programs, proof points and action-led navigation.', result: 'A simpler customer journey from first visit to first lesson.', services: ['Conversion design', 'Content structure', 'Responsive website'] },
    { image: Project07, alt: 'AutoClean car care website concept', title: 'AutoClean Järfälla', category: 'Automotive', challenge: 'Differentiate a local car-care business in a crowded and price-driven market.', solution: 'A premium dark visual identity that highlights precision, services and booking.', result: 'A sharper market position with a clearer route to customer enquiries.', services: ['Brand direction', 'Website design', 'Lead generation'] },
    { image: Project12, alt: 'Tegelbacken restaurant website project', title: 'Tegelbacken', category: 'Food & delivery', challenge: 'Make menu discovery visual, fast and comfortable across devices.', solution: 'A bright food-first interface with simple categories and prominent ordering actions.', result: 'A more engaging menu experience designed to support online orders.', services: ['E-commerce UX', 'Responsive design', 'Frontend development'] },
  ],
  [
    { image: Project09, alt: 'CasaNova real estate website project', title: 'CasaNova', category: 'Real estate', challenge: 'Present premium properties without overwhelming visitors with information.', solution: 'A cinematic property experience with strong search, clear navigation and focused enquiries.', result: 'A high-value digital presentation designed to connect buyers with suitable properties.', services: ['UX design', 'Property discovery', 'Frontend development'] },
    { image: Project10, alt: 'Airport operations dashboard project', title: 'Airport Scheduler', category: 'Operations platform', challenge: 'Bring complex flight, staffing and operational information into one usable workspace.', solution: 'A role-based dashboard for live departures, planning and operational decision support.', result: 'Faster overview and a foundation for more efficient daily airport operations.', services: ['Product strategy', 'Dashboard UX', 'Full-stack development'] },
    { image: Project11, alt: 'RattNu consumer rights platform project', title: 'RättNu', category: 'Legal technology', challenge: 'Make consumer rights understandable and help users take the correct next step.', solution: 'A guided platform that turns complex situations into clear, manageable actions.', result: 'A calmer and more accessible path from problem to structured complaint.', services: ['Service design', 'Guided flows', 'Web application'] },
    { image: Project08, alt: 'Stockholms Platt and Matt website concept', title: 'Stockholms Platt & Matt', category: 'Construction', challenge: 'Build trust quickly and explain specialist construction services clearly.', solution: 'An editorial business website combining craftsmanship, proof points and direct enquiry paths.', result: 'A credible premium presence built to generate qualified project enquiries.', services: ['Brand positioning', 'Website design', 'Lead generation'] },
  ],
];

const directions = ['from-top', 'from-bottom', 'from-left', 'from-right'];
const effectCount = 4;

const Project = () => {
  const [activeGroup, setActiveGroup] = useState(0);
  const [effectIndex, setEffectIndex] = useState(0);
  const [isChanging, setIsChanging] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const changeTimeoutRef = useRef(null);
  const caseStudyOpenRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      const galleryItems = document.querySelectorAll('.gallery-item');
      galleryItems.forEach((item) => {
        const rect = item.getBoundingClientRect();
        // Check if the element is in the viewport
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          item.classList.add('in-view');
        } else {
          item.classList.remove('in-view'); // Remove the class if out of view
        }
      });
    };

    // Trigger scroll handler on page load to check initial visibility
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeGroup]);

  useEffect(() => {
    projectGroups.flat().forEach(({ image }) => {
      const preloadImage = new Image();
      preloadImage.src = image;
    });

    const interval = window.setInterval(() => {
      if (document.hidden || caseStudyOpenRef.current) return;

      setEffectIndex((currentEffect) => (currentEffect + 1) % effectCount);
      setIsChanging(true);
      changeTimeoutRef.current = window.setTimeout(() => {
        setActiveGroup((currentGroup) => (currentGroup + 1) % projectGroups.length);
        setIsChanging(false);
      }, 720);
    }, 4000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(changeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    caseStudyOpenRef.current = Boolean(selectedProject);
    if (!selectedProject) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setSelectedProject(null);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedProject]);

  return (
    <div className="project-container" id="project">
      <div className="project-info">
        <div
          className={`gallery project-gallery-swap effect-${effectIndex}${isChanging ? ' is-changing' : ''}`}
        >
          {projectGroups[activeGroup].map((project, index) => (
            <button
              type="button"
              className={`gallery-item ${directions[index]}`}
              key={`${activeGroup}-${index}`}
              onClick={() => setSelectedProject(project)}
              aria-label={`View case study: ${project.title}`}
            >
              <img
                className="project-image-enter"
                src={project.image}
                alt={project.alt}
                loading="lazy"
                decoding="async"
              />
              <span className="project-card-overlay">
                <span>
                  <small>{project.category}</small>
                  <strong>{project.title}</strong>
                </span>
                <span className="project-card-action">View case study <b aria-hidden="true">↗</b></span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {selectedProject && (
        <div className="case-study-modal" role="dialog" aria-modal="true" aria-labelledby="case-study-title" onMouseDown={() => setSelectedProject(null)}>
          <article className="case-study-panel" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" className="case-study-close" onClick={() => setSelectedProject(null)} aria-label="Close case study">×</button>
            <div className="case-study-visual">
              <img src={selectedProject.image} alt={selectedProject.alt} />
            </div>
            <div className="case-study-content">
              <p className="case-study-eyebrow">{selectedProject.category} · Selected work</p>
              <h2 id="case-study-title">{selectedProject.title}</h2>
              <div className="case-study-facts">
                <section><span>01</span><h3>Challenge</h3><p>{selectedProject.challenge}</p></section>
                <section><span>02</span><h3>Solution</h3><p>{selectedProject.solution}</p></section>
                <section><span>03</span><h3>Result</h3><p>{selectedProject.result}</p></section>
              </div>
              <div className="case-study-services">
                {selectedProject.services.map((service) => <span key={service}>{service}</span>)}
              </div>
              <a href="#contact" className="case-study-cta" onClick={() => setSelectedProject(null)}>Start a similar project <span aria-hidden="true">→</span></a>
            </div>
          </article>
        </div>
      )}
    </div>
  );
};

export default Project;
